import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Save } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  {
    key: "motivo_consulta", title: "Motivo de consulta", others: "motivo_otros",
    options: ["Caries", "Limpieza", "Valoración", "Dolor", "Sangrado", "Sensibilidad", "Estética", "Ortodoncia", "Urgencia"],
  },
  {
    key: "enfermedad_actual", title: "Enfermedad actual", others: "enfermedad_otros",
    options: ["Enfermedad periodontal", "Caries activa", "Bruxismo", "Maloclusión", "Edentulismo parcial", "Edentulismo total"],
  },
  {
    key: "alertas_medicas", title: "Alertas médicas", others: "alertas_otros",
    options: ["Alergia a alimentos", "Alergia a medicamentos", "Hipertensión", "Diabetes", "Asma",
      "Cardiopatía", "Anticoagulado", "Inmunosuprimido", "Hepatitis", "VIH", "Epilepsia", "Embarazo"],
  },
  {
    key: "medicamentos", title: "Medicamentos", others: "medicamentos_otros",
    options: ["Antibióticos", "Anticonvulsivos", "Antidepresivos", "Antiglucemiantes", "Antihipertensivos",
      "Bifosfonatos", "Diuréticos", "Anticoagulantes", "Corticoides", "Hormonales"],
  },
  {
    key: "habitos", title: "Hábitos", others: "habitos_otros",
    options: ["Alcohol", "Bruxismo", "Drogas", "Tabaquismo", "Onicofagia (morder uñas)", "Empuje lingual", "Respirador bucal"],
  },
];

export default function AnamnesisForm({ patientId }) {
  const [data, setData] = useState({
    motivo_consulta: [], motivo_otros: "",
    enfermedad_actual: [], enfermedad_otros: "",
    alertas_medicas: [], alertas_otros: "",
    medicamentos: [], medicamentos_otros: "",
    habitos: [], habitos_otros: "",
    embarazo: false, fumador: false,
    notas_generales: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/patients/${patientId}/anamnesis`).then((r) => {
      if (cancel) return;
      setData((prev) => ({ ...prev, ...r.data }));
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { cancel = true; };
  }, [patientId]);

  const toggle = (k, opt) => {
    const arr = data[k] || [];
    setData({ ...data, [k]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${patientId}/anamnesis`, data);
      toast.success("Ficha guardada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-12 text-center">Cargando ficha…</div>;

  return (
    <div className="space-y-5" data-testid="anamnesis-form">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Ficha Anamnesis</h2>
          <p className="text-sm text-muted-foreground">Cuestionario de antecedentes y motivo de consulta.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="anamnesis-save">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar ficha"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SECTIONS.map((s) => (
          <div key={s.key} className="card-flat p-5 space-y-3" data-testid={`section-${s.key}`}>
            <div className="font-display font-bold text-base tracking-tight">{s.title}</div>
            <div className="grid grid-cols-2 gap-y-2 gap-x-3">
              {s.options.map((opt) => {
                const checked = (data[s.key] || []).includes(opt);
                return (
                  <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer hover:text-primary">
                    <input
                      type="checkbox" checked={checked}
                      onChange={() => toggle(s.key, opt)}
                      className="w-4 h-4 accent-primary"
                      data-testid={`chk-${s.key}-${opt}`}
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Otros / detalles</label>
              <textarea
                rows={2} className="input-flat h-auto py-2 text-sm w-full"
                value={data[s.others] || ""}
                onChange={(e) => setData({ ...data, [s.others]: e.target.value })}
              />
            </div>
          </div>
        ))}

        <div className="card-flat p-5 space-y-3 lg:col-span-2">
          <div className="font-display font-bold text-base tracking-tight">Otros antecedentes y notas</div>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!data.embarazo} onChange={(e) => setData({ ...data, embarazo: e.target.checked })} className="w-4 h-4 accent-primary" />
              Embarazo activo
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!data.fumador} onChange={(e) => setData({ ...data, fumador: e.target.checked })} className="w-4 h-4 accent-primary" />
              Fumador(a)
            </label>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Notas generales del clínico</label>
            <textarea
              rows={4} className="input-flat h-auto py-2 text-sm w-full"
              value={data.notas_generales || ""}
              onChange={(e) => setData({ ...data, notas_generales: e.target.value })}
              data-testid="anamnesis-notes"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
